// 1. Core & External Libraries
import { format, isSameMonth, parseISO, startOfDay, startOfMonth } from 'date-fns';
import { memo, useCallback, useMemo, useState } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

// 2. Layout & shared UI
import { Stack } from '@/components/layout';
import { Button, Text } from '@/components/ui';

// 3. Feature (guardian)
import FamilyCalendar from '@/features/guardian/components/family-calendar';
import CalendarControls from '@/features/guardian/components/family-calendar/calendar-controls';
import CalendarListView from '@/features/guardian/components/family-calendar/calendar-list-view';
import {
  filterEventsByPlayer,
  getFutureFamilyEvents,
  toCalendarPlayers,
  type CalendarViewMode,
} from '@/features/guardian/components/family-calendar/screen-utils';
import { useFamilyEvents } from '@/features/guardian/hooks/use-family-events';
import { useOpenEventDetail } from '@/features/guardian/hooks/use-open-event-detail';

// 4. Hooks, strings, theme
import { STRINGS } from '@/constants/strings';
import { createStyles, useGuardianStackNavigation, useScaling } from '@/hooks';
import { colors, guardianColors, withOpacity } from '@/theme/colors';

const strings = STRINGS.guardian.kids;

/**
 * Family Calendar (mobile only) — PP-50x.
 *
 * Two view modes toggled by the top-right icon:
 *  • Calendar: the shared {@link FamilyCalendar} month grid (same component the tablet
 *    Kids screen uses), navigated month by month with the ‹ › controls.
 *  • List: every upcoming event grouped by date (BR-05: no 10-day cap).
 *
 * The filter chips render the guardian's real players; all are shown by default. Tapping
 * Event rows open the shared Event Detail surface (PP-502).
 *
 * NOTE: Daily View (weekly hourly timeline + current-time indicator) is intentionally
 * deferred. BR-04 says it opens by tapping a date in the Calendar view, so
 * {@link handleSelectDay} is wired to the selection state and left as the hook for it.
 */
function GuardianFamilyCalendarScreen() {
  const { scale } = useScaling();
  const styles = useStyles();
  const insets = useSafeAreaInsets();
  const navigation = useGuardianStackNavigation();
  const { players: guardianPlayers, events } = useFamilyEvents();
  const handleEventPress = useOpenEventDetail();

  const [today] = useState(() => startOfDay(new Date()));
  const [mode, setMode] = useState<CalendarViewMode>('calendar');
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(null);
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(today));
  const [selectedDate, setSelectedDate] = useState(() => today);

  const players = useMemo(() => toCalendarPlayers(guardianPlayers), [guardianPlayers]);

  const playerEvents = useMemo(
    () => filterEventsByPlayer(events, selectedPlayer),
    [events, selectedPlayer],
  );
  const monthEvents = useMemo(
    () => playerEvents.filter(event => isSameMonth(parseISO(event.date), visibleMonth)),
    [playerEvents, visibleMonth],
  );
  const listEvents = useMemo(
    () => getFutureFamilyEvents(events, today, selectedPlayer),
    [events, selectedPlayer, today],
  );

  const contentBottomInset = insets.bottom + scale(24);

  const handleBack = useCallback(() => navigation.goBack(), [navigation]);

  // BR-04: the Daily View is reachable only from here — never from the view toggle.
  const handleSelectDay = useCallback(
    (day: Date) => {
      setSelectedDate(day);
      navigation.navigate('GuardianFamilyCalendarDay', {
        date: format(day, 'yyyy-MM-dd'),
        playerId: selectedPlayer ?? undefined,
      });
    },
    [navigation, selectedPlayer],
  );

  return (
    <Stack flex={1} backgroundColor={guardianColors['1']}>
      <Stack
        direction="row"
        align="center"
        gap={scale(8)}
        paddingTop={insets.top + scale(8)}
        paddingX={scale(20)}>
        <Button variant="ghost" onPress={handleBack} style={styles.backButton}>
          <Stack
            viewType="liquid-glass"
            align="center"
            justify="center"
            backgroundColor={withOpacity(colors['0'], 0.3)}
            overlayColor={withOpacity(colors['0'], 0.3)}
            intensity={20}
            tint="light"
            style={styles.backButtonGlass}>
            <Button.Icon
              name="IcBackBlue"
              color={guardianColors['10']}
              styleWrapper={styles.backButtonIcon}
            />
          </Stack>
        </Button>
        <Stack>
          <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.eyebrow}>
            {strings.calendarTitle}
          </Text>
          <Text variant="Font Guardian App/Heading/Heading 20" style={styles.year}>
            {format(visibleMonth, 'yyyy')}
          </Text>
        </Stack>
      </Stack>

      <CalendarControls
        players={players}
        selectedPlayer={selectedPlayer}
        onSelectPlayer={setSelectedPlayer}
        mode={mode}
        onChangeMode={setMode}
      />

      {mode === 'calendar' ? (
        <Stack flex={1} paddingX={scale(20)} paddingBottom={contentBottomInset}>
          <FamilyCalendar
            weekdayPlacement="outside"
            month={visibleMonth}
            selectedDate={selectedDate}
            today={today}
            events={monthEvents}
            onChangeMonth={setVisibleMonth}
            onSelectDay={handleSelectDay}
          />
        </Stack>
      ) : (
        <CalendarListView
          events={listEvents}
          onEventPress={handleEventPress}
          contentBottomInset={contentBottomInset}
        />
      )}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  backButton: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(20),
    padding: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    shadowColor: 'transparent',
    elevation: 0,
  },
  backButtonGlass: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(20),
    paddingTop: scale(10),
    paddingRight: scale(12),
    paddingBottom: scale(10),
    paddingLeft: scale(10),
  },
  backButtonIcon: {
    marginRight: 0,
  },
  eyebrow: {
    color: guardianColors['6'],
  },
  year: {
    color: guardianColors['10'],
  },
}));

export { GuardianFamilyCalendarScreen };
export default memo(GuardianFamilyCalendarScreen);
